/**
 * BasicInfoFields - Basic aircraft info form fields
 *
 * Fields: tail_number, aircraft_type_id, manufacturer, model, status
 */

import type { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AircraftTypeAutocomplete } from '@/components/aircraft-types/AircraftTypeAutocomplete'
import type { AircraftTypeRow } from '@/types/aircraft-types'
import type { AircraftFormValues } from '../aircraft-form-schema'
import { AIRCRAFT_STATUSES, AIRCRAFT_STATUS_LABELS } from '@/types/aircrafts'

interface BasicInfoFieldsProps {
  form: UseFormReturn<AircraftFormValues>
  disabled?: boolean
  /** Include tail number field (false for quick edit) */
  includeTailNumber?: boolean
  /** Include status field (false for quick edit) */
  includeStatus?: boolean
  /** Include aircraft type selector (false for quick edit) */
  includeAircraftType?: boolean
  /** Callback when aircraft type is selected */
  onAircraftTypeSelect?: (aircraftType: AircraftTypeRow) => void
}

export function BasicInfoFields({
  form,
  disabled = false,
  includeTailNumber = true,
  includeStatus = true,
  includeAircraftType = true,
  onAircraftTypeSelect,
}: BasicInfoFieldsProps) {
  const handleAircraftTypeSelect = (aircraftType: AircraftTypeRow) => {
    form.setValue('aircraft_type_id', aircraftType.id, { shouldDirty: true })
    form.setValue('manufacturer', aircraftType.manufacturer, { shouldDirty: true })
    form.setValue('model', aircraftType.model, { shouldDirty: true })
    onAircraftTypeSelect?.(aircraftType)
  }

  return (
    <div className="space-y-4">
      {includeTailNumber && (
        <FormField
          control={form.control}
          name="tail_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tail Number *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="N12345"
                  className="font-mono uppercase"
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  disabled={disabled}
                />
              </FormControl>
              <FormDescription>Aircraft registration (N-number)</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {includeAircraftType && (
        <FormField
          control={form.control}
          name="aircraft_type_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Aircraft Type</FormLabel>
              <FormControl>
                <AircraftTypeAutocomplete
                  onSelect={handleAircraftTypeSelect}
                  value={field.value}
                  disabled={disabled}
                />
              </FormControl>
              <FormDescription>Select from aircraft type library</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="manufacturer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Manufacturer</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  placeholder="Cessna"
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="model"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Model</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  placeholder="172"
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {includeStatus && (
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={disabled}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {AIRCRAFT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {AIRCRAFT_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}
